/* eslint-disable no-console */
import React, { createContext, useCallback, useEffect, useState } from "react";
import { userApis } from "@payping/api";
import { useAsync } from "react-use";
import { dateFormats, toJalali } from "@payping/utils/dateUtils";
import { useAppSelector } from "../redux";
import { useAppTranslation } from "../useAppTranslation";

interface GoftinoContextType {
  openChat: () => void;
  setUser: (userInfo: GoftinoUser, callback?: (status: { status: string }) => void) => void;
  setUserId: (userId: string, callback?: (status: { status: string }) => void) => void;
  isLoaded: boolean;
}

interface GoftinoUser {
  email?: string;
  name?: string;
  about?: string;
  phone?: string;
  avatar?: string;
  tags?: string;
  forceUpdate?: boolean;
}

const loadGoftinoScript = (chatId: string): Promise<void> =>
  new Promise((resolve, reject) => {
    if (window.Goftino) {
      resolve(); // If already loaded, resolve immediately
      return;
    }

    if (document.getElementById("goftino-script")) {
      const existingScript = document.getElementById("goftino-script");
      existingScript?.addEventListener("load", () => resolve());
      existingScript?.addEventListener("error", () => reject(new Error("Failed to load Goftino script.")));
      return;
    }

    const script = document.createElement("script");
    script.id = "goftino-script";
    script.async = true;
    script.src = `https://www.goftino.com/widget/${chatId}`;

    const localData = localStorage.getItem(`goftino_${chatId}`);
    if (localData) {
      script.src += `?o=${localData}`;
    }

    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load Goftino script."));
    document.head.appendChild(script);
  });

export const GoftinoContext = createContext<GoftinoContextType | null>(null);

export const GoftinoProvider: React.FC<{ chatId: string; children: React.ReactNode }> = ({ chatId, children }) => {
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const planTypeLabel = useAppSelector((state) => state.user.panelInfo?.currentPlan?.title);
  const user = useAppSelector((state) => state.user);

  const { t } = useAppTranslation();

  useEffect(() => {
    let isMounted = true;

    loadGoftinoScript(chatId)
      .then(() => {
        if (isMounted) {
          setIsScriptLoaded(true);
        }
      })
      .catch((error) => {
        console.error(error);
        if (isMounted) {
          setIsScriptLoaded(false);
        }
      });

    return () => {
      const script = document.getElementById("goftino-script");
      if (script) {
        script.remove();
      }
      isMounted = false;
    };
  }, [chatId]);

  const openChat = useCallback(() => {
    if (window.Goftino) {
      window.Goftino.open();
    } else {
      // eslint-disable-next-line no-console
      console.warn("Goftino is not yet loaded.");
    }
  }, []);

  const setUser = useCallback(
    (userInfo: GoftinoUser, callback?: (status: { status: string }) => void) => {
      if (!isScriptLoaded) {
        // eslint-disable-next-line no-console
        console.warn("Goftino script is not yet loaded. Cannot set user.");
        return;
      }

      if (window.Goftino && typeof window?.Goftino?.setUser === "function") {
        window.Goftino.setUser(userInfo, callback);
      } else {
        // eslint-disable-next-line no-console
        console.warn("Goftino.setUser is not available.");
      }
    },
    [isScriptLoaded],
  );

  const setUserId = useCallback(
    (userId: string, callback?: (status: { status: string }) => void) => {
      if (!isScriptLoaded) {
        // eslint-disable-next-line no-console
        console.warn("Goftino script is not yet loaded. Cannot set user ID.");
        return;
      }

      if (window.Goftino && typeof window?.Goftino?.setUserId === "function") {
        window.Goftino.setUserId(userId, callback);
      } else {
        // eslint-disable-next-line no-console
        console.warn("Goftino.setUserId is not available.");
      }
    },
    [isScriptLoaded],
  );

  const chatDataState = useAsync(async () => userApis.crispGet(), [isScriptLoaded, setUser]);

  useEffect(() => {
    if (!isScriptLoaded || !user) return;
    if (user?.id !== undefined) setUserId(user?.id.toString());
    if (chatDataState.error || chatDataState.loading) {
      setUser({
        name: `${planTypeLabel} - ${user.personalInfo?.firstName ?? user?.firstName ?? ""} ${
          user.personalInfo?.lastName ?? user?.lastName ?? ""
        }`,
        avatar: user?.profilePicture ?? user.personalInfo?.profilePicture ?? undefined,
        email: user.personalInfo?.email ?? undefined,
        about:
          `${t("common.registerDate")}` +
          ` : ${
            toJalali({
              date: user.registerDateTime,
              dateFormat: dateFormats.standard.utcDateTime,
              toFormat: dateFormats.ui.jalaliDateTime,
            }).string
          }\n` +
          `${t("common.userId")}` +
          ` : ${(user.id ?? user.personalInfo?.id ?? "")?.toString()}\n` +
          `${t("common.username2")}` +
          ` : ${(user?.username ?? user.personalInfo?.username ?? "")?.toString()}`,
        forceUpdate: true,
      });
      return;
    }

    setUser({
      name: `${planTypeLabel} - ${
        chatDataState.value?.fullName
          ? chatDataState.value.fullName
          : `${user.personalInfo?.firstName ?? user?.firstName ?? ""} ${
              user.personalInfo?.lastName ?? user?.lastName ?? ""
            }`
      }`,
      avatar:
        chatDataState.value?.profilePicture ?? user?.profilePicture ?? user.personalInfo?.profilePicture ?? undefined,
      email: chatDataState.value?.email ?? user.personalInfo?.email ?? undefined,
      about:
        `${t("common.registerDate")}` +
        ` : ${
          toJalali({
            date: user.registerDateTime,
            dateFormat: dateFormats.standard.utcDateTime,
            toFormat: dateFormats.ui.jalaliDateTime,
          }).string
        }\n` +
        `${t("common.userId")}` +
        ` : ${(chatDataState.value?.userId ?? user?.id ?? user.personalInfo?.id ?? "")?.toString()}\n` +
        `${t("common.username2")}` +
        ` : ${(chatDataState.value?.username ?? user?.username ?? user.personalInfo?.username ?? "")?.toString()}`,

      forceUpdate: true,
    });
  }, [isScriptLoaded, chatDataState, user, setUserId, setUser, planTypeLabel, t]);

  return (
    <GoftinoContext.Provider value={{ openChat, setUser, setUserId, isLoaded: isScriptLoaded }}>
      {children}
    </GoftinoContext.Provider>
  );
};
